import React, { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPhone } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { MyContext } from '../../../../Context/MyContext';
import ImageWithLoader from '../../../../Config/ImageWithLoader';
import { formatStateInCityFromIds } from '../../../../utils/locationFormat';
import { hasValidPropertyCoords } from '../../../../utils/propertyCoords';
import { getPrimaryPropertyContact } from '../../../../utils/propertyContacts';
import './NearbyListingCard.css';

const fmtPrice = (p) => {
  if (!p && p !== 0) return 'Price on request';
  if (p >= 10000000) return `₹${(p / 10000000).toFixed(2)} Cr`;
  if (p >= 100000) return `₹${(p / 100000).toFixed(2)} L`;
  return `₹${p.toLocaleString('en-IN')}`;
};

export const NearbyListingCardSkeleton = () => (
  <div className="nlc-card nlc-card--skeleton" aria-hidden>
    <div className="nlc-card__media nlc-skel-block" />
    <div className="nlc-card__content">
      <div className="nlc-skel-line nlc-skel-line--title" />
      <div className="nlc-skel-line nlc-skel-line--sub" />
      <div className="nlc-skel-line nlc-skel-line--price" />
      <div className="nlc-skel-tags">
        <div className="nlc-skel-pill nlc-skel-pill--sm" />
        <div className="nlc-skel-pill nlc-skel-pill--sm" />
      </div>
      <div className="nlc-skel-footer">
        <div className="nlc-skel-pill" />
        <div className="nlc-skel-pill" />
      </div>
    </div>
  </div>
);

const NearbyListingCard = ({ property, showDistance = true, onContactClick }) => {
  const { cities, states, categorys, imgUrl, partners, admins } = useContext(MyContext);
  const navigate = useNavigate();

  const locationLabel = formatStateInCityFromIds(
    property.state_id,
    property.city_id,
    states,
    cities,
  );

  const category = categorys?.find(
    (c) => Number(c.id ?? c.category_id) === Number(property.category_id),
  );

  const images = useMemo(() => {
    const raw = [property.thumbnail, ...(property.slider || [])].filter(Boolean);
    return [...new Set(raw)].map((p) => imgUrl(p));
  }, [property.thumbnail, property.slider, imgUrl]);

  const thumb = images[0] || null;
  const imageCount = images.length;

  const distance = property.distance_km != null ? `${property.distance_km} km` : null;
  const hasMap = hasValidPropertyCoords(property);

  const contact = useMemo(
    () => getPrimaryPropertyContact(property, partners, admins),
    [property, partners, admins],
  );

  const title = property.extra_fields?.project_name
    || category?.category_name
    || 'Property';

  const subtitleParts = [
    property.bhk ? `${property.bhk} BHK` : null,
    category?.category_name && title !== category?.category_name
      ? category?.category_name
      : null,
  ].filter(Boolean);

  const availabilityLabel = property.property_availability
    || property.listing_type
    || (property.is_project ? 'Project' : null);

  const handleCardClick = () => navigate(`/property/${property.id}`);

  const handleContact = (e) => {
    e.stopPropagation();
    if (onContactClick) {
      onContactClick(property);
      return;
    }
    toast.info('Contact is not available from this view');
  };

  return (
    <article
      className="nlc-card"
      onClick={handleCardClick}
      onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
      role="button"
      tabIndex={0}
    >
      <div className="nlc-card__media">
        {thumb ? (
          <ImageWithLoader src={thumb} alt="" className="nlc-card__img" />
        ) : (
          <div className="nlc-card__img-placeholder">🏠</div>
        )}

        <div className="nlc-card__badges">
          {category?.category_name && (
            <span className="nlc-card__badge">{category.category_name}</span>
          )}
          {showDistance && distance && (
            <span className="nlc-card__badge nlc-card__badge--dist">{distance}</span>
          )}
        </div>

        {imageCount > 1 && (
          <span className="nlc-card__photo-count">1/{imageCount}</span>
        )}
      </div>

      <div className="nlc-card__content">
        <div className="nlc-card__head">
          <div className="nlc-card__head-text">
            <h3 className="nlc-card__title">{title}</h3>
            {subtitleParts.length > 0 && (
              <p className="nlc-card__subtitle">{subtitleParts.join(' · ')}</p>
            )}
            {locationLabel && (
              <p className="nlc-card__location">{locationLabel}</p>
            )}
          </div>
          {availabilityLabel && (
            <span className="nlc-card__ribbon">{availabilityLabel}</span>
          )}
        </div>

        <div className="nlc-card__price-block">
          <span className="nlc-card__price-label">
            {property.bhk ? `${property.bhk} BHK` : category?.category_name || 'Price'}
          </span>
          <span className="nlc-card__price">{fmtPrice(property.price)}</span>
        </div>

        <div className="nlc-card__meta">
          {property.bhk ? <span className="nlc-card__tag">{property.bhk} BHK</span> : null}
          {property.buildup_area ? (
            <span className="nlc-card__tag">{property.buildup_area} sqft</span>
          ) : null}
          {property.listing_type && (
            <span className="nlc-card__tag">{property.listing_type}</span>
          )}
          {hasMap && <span className="nlc-card__tag nlc-card__tag--accent">GPS verified</span>}
        </div>

        {property.seo_description && (
          <p className="nlc-card__desc">{property.seo_description}</p>
        )}

        <div className="nlc-card__footer">
          <div className="nlc-card__agent">
            <span className="nlc-card__agent-label">Contact</span>
            <span className="nlc-card__agent-name">{contact?.name || 'Property Advisor'}</span>
          </div>
          <div className="nlc-card__actions">
            <button
              type="button"
              className="nlc-card__btn nlc-card__btn--outline"
              onClick={handleContact}
            >
              <FiPhone size={15} />
              Call
            </button>
            <button
              type="button"
              className="nlc-card__btn nlc-card__btn--primary"
              onClick={handleContact}
            >
              <FaWhatsapp size={16} />
              WhatsApp
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default NearbyListingCard;
